import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Printer, Eye } from 'lucide-react';
import PrintCandidateProfile from './PrintCandidateProfile';
import { useReactToPrint } from 'react-to-print';

interface PrintCandidateDialogProps {
  candidate: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PrintCandidateDialog = ({ candidate, open, onOpenChange }: PrintCandidateDialogProps) => {
  const [isAnonymized, setIsAnonymized] = useState(false);
  const [showLogo, setShowLogo] = useState(true);
  const [showAISummary, setShowAISummary] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Candidate_Profile_${isAnonymized ? 'Anonymized' : candidate?.first_name}_${candidate?.last_name}`,
  });

  if (!candidate) return null;

  const candidateName = `${candidate.first_name} ${candidate.last_name}`;
  const hasAISummary = candidate.ai_skills_summary || candidate.ai_products_summary || candidate.candidate_ai_insights?.[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={showPreview ? "max-w-4xl max-h-[90vh]" : "sm:max-w-md"}>
        <DialogHeader>
          <DialogTitle>Print Candidate Profile</DialogTitle>
          <DialogDescription>
            Configure print options for {isAnonymized ? 'Anonymized Candidate' : candidateName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="anonymized">Anonymize Profile</Label>
              <p className="text-sm text-muted-foreground">
                Hide contact info, names, and institution names
              </p>
            </div>
            <Switch
              id="anonymized"
              checked={isAnonymized}
              onCheckedChange={setIsAnonymized}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="showLogo">Include Company Logo</Label>
              <p className="text-sm text-muted-foreground">
                Add company branding to the header
              </p>
            </div>
            <Switch
              id="showLogo"
              checked={showLogo}
              onCheckedChange={setShowLogo}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="showAISummary">Include AI Summary</Label>
              <p className="text-sm text-muted-foreground">
                {hasAISummary 
                  ? 'Add AI-generated professional summary'
                  : 'No AI summary available for this candidate'
                }
              </p>
            </div>
            <Switch
              id="showAISummary"
              checked={showAISummary}
              onCheckedChange={setShowAISummary}
              disabled={!hasAISummary}
            />
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowPreview(!showPreview)}
            >
              <Eye className="mr-2 h-4 w-4" />
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </Button>
            <Button
              className="flex-1"
              onClick={() => handlePrint()}
            >
              <Printer className="mr-2 h-4 w-4" />
              Print
            </Button>
          </div>
        </div>

        {showPreview && (
          <div className="border rounded-lg overflow-auto max-h-[60vh] bg-white">
            <PrintCandidateProfile
              ref={printRef}
              candidate={candidate}
              isAnonymized={isAnonymized}
              showLogo={showLogo}
              showAISummary={showAISummary}
            />
          </div>
        )}

        {/* Hidden print content */}
        {!showPreview && (
          <div className="hidden">
            <PrintCandidateProfile
              ref={printRef}
              candidate={candidate}
              isAnonymized={isAnonymized}
              showLogo={showLogo}
              showAISummary={showAISummary}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PrintCandidateDialog;
